/* eslint-disable react/prop-types */
import { useRouter } from "next/router";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import api from "@api/index";

import { Button } from "./Button";
import ArrowLeftIcon from "./Icons/ArrowLeft";
import Icon from "./Icons/Icon";
import WarningIcon from "./Icons/Warning";
import Modal from "./Modal";
import Radio from "./Radio";

import { useGetBrandIdRedirect } from "@hooks/useGetBrandIdRedirect";
import { useConfigurationPathname } from "@hooks/useConfigurationPathname";
import { useRoutingUtils } from "@hooks/useRoutingUtils";

import { getBrandIdLoginRedirect } from "@lib/getBrandIdLoginRedirect";
import { getWithdrawalUrl } from "@lib/getWithdrawalUrl";
import { Trans, useTranslation } from "@lib/i18n";

import {
  useBrandIdSettingsSelector,
  useWithdrawalSettingsSelector,
} from "@redux/reducers/appConfig";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";

import { ROUTES } from "@shared/routing/routes";
import { redirectToExternalURL } from "@shared/v2/lib/redirectToExternalURL";
import { REQUEST_ERROR_CODES } from "@shared/constants";

const WithdrawalModalContext = createContext();

export const WithdrawalModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <WithdrawalModalContext.Provider value={{ isOpen, setIsOpen }}>
      <WithdrawalModal />
      {children}
    </WithdrawalModalContext.Provider>
  );
};

export const useSetWithdrawalModalOpenContext = () => {
  const ctx = useContext(WithdrawalModalContext);

  if (!ctx) {
    throw new Error("Use this hook inside WithdrawalModalContextProvider");
  }

  return ctx.setIsOpen;
};

const PAGE = {
  SELECTION: "SELECTION",
  FIND_ORDER: "FIND_ORDER",
  B2B_ORDER: "B2B_ORDER",
};

const OPTION = {
  ACCOUNT: "iHaveAnAccount",
  FIND_ORDER: "findOrder",
  MANUAL_INPUT: "manualInput",
};

const WithdrawalModal = () => {
  const { isOpen, setIsOpen } = useContext(WithdrawalModalContext);

  const {
    i18n: { language },
    t,
  } = useTranslation();
  const router = useRouter();
  const { handleSubmit, register } = useForm();

  const brandIDConfig = useBrandIdSettingsSelector();
  const configurationPathname = useConfigurationPathname();
  const getBrandIdRedirect = useGetBrandIdRedirect();
  const isUserTrusted = useIsUserTrustedSelector();
  const routingUtils = useRoutingUtils();
  const withdrawalSettings = useWithdrawalSettingsSelector();

  const myOrdersReturnUrl = getBrandIdRedirect(ROUTES.MY_ACCOUNT_ORDERS);

  const [selectedOption, setSelectedOption] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const [page, setPage] = useState(PAGE.SELECTION);
  const [isSearchError, setIsSearchError] = useState(false);

  const handleContinueClick = async () => {
    setIsWorking(true);

    if (selectedOption === OPTION.ACCOUNT) {
      if (router.pathname === ROUTES.MY_ACCOUNT_ORDERS) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setIsOpen(false);
        return;
      }

      if (isUserTrusted) {
        const localizedPathname = routingUtils.getLocalizedPathname(
          ROUTES.MY_ACCOUNT_ORDERS,
          null,
          language
        );

        await router.push(ROUTES.MY_ACCOUNT_ORDERS, localizedPathname);
        setIsOpen(false);
        return;
      }

      return redirectToExternalURL(
        getBrandIdLoginRedirect(myOrdersReturnUrl, brandIDConfig, language)
      );
    }

    if (selectedOption === OPTION.FIND_ORDER) {
      setPage(PAGE.FIND_ORDER);
      setIsWorking(false);
      return;
    }

    if (selectedOption === OPTION.MANUAL_INPUT) {
      setIsWorking(true);
      return redirectToExternalURL(withdrawalSettings.link);
    }
  };

  const handleBackClick = () => {
    if (!isWorking) {
      setPage(PAGE.SELECTION);
      setSelectedOption(null);
    }
  };

  const handleExitClick = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (router.pathname === ROUTES.INDEX) {
      setIsOpen(false);
      return;
    }

    await router.push(ROUTES.CONFIG, configurationPathname);
    setIsOpen(false);
  };

  const handleFormSubmit = async data => {
    const { orderId, email } = data;

    if (!orderId || !email) {
      return;
    }

    try {
      setIsWorking(true);
      setIsSearchError(false);

      const orderData = await api.getOrderByIdAndEmail(orderId, email);

      if (orderData.data.success) {
        const { withdrawalData } = orderData.data;

        if (!withdrawalData) {
          throw new Error(
            "Lead lookup request successful but no withdrawal data received"
          );
        }

        const targetUrl = getWithdrawalUrl(withdrawalSettings.link, {
          ...withdrawalData,
          email,
          orderId,
        });

        redirectToExternalURL(targetUrl);
      } else if (
        orderData.data.errorCode === REQUEST_ERROR_CODES.ORDER_IS_B2B
      ) {
        setPage(PAGE.B2B_ORDER);
        setIsWorking(false);
        setIsSearchError(false);
        return;
      } else {
        throw new Error(
          "Lead lookup response ok, but success is false and reason unrecognized"
        );
      }
    } catch (e) {
      console.error("Error fetching order with provided details", e);
      setIsSearchError(true);
      setIsWorking(false);
      return;
    }
  };

  useEffect(() => {
    if (router.query?.withdrawal === "true") {
      setIsOpen(true);
    }
  }, [router.query]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      setPage(PAGE.SELECTION);
      setIsWorking(false);
    }
  }, [isOpen]);

  return (
    <Modal
      ariaLabel={t("withdrawalModal.title")}
      isOpen={isOpen}
      doNotRenderInvisible
      onClose={() => setIsOpen(false)}
      withCloseIcon={true}
    >
      <div className="flex flex-col gap-4 pb-4">
        {page === PAGE.SELECTION && (
          <>
            <h1 className="text-4xl/snug font-semibold uppercase m-0 mb-4 text-black pr-8">
              {t("withdrawalModal.title")}
            </h1>
            <p className="text-primaryDark m-0 [&+&]:mt-6 sm:text-base/snug">
              {t("withdrawalModal.description.selection")}
            </p>
            <div className="flex flex-col w-full gap-4 pt-2">
              {Object.values(OPTION).map(option => {
                const isSelected = selectedOption === option;

                return (
                  <label
                    className={twMerge(
                      "rounded-md px-2 py-4 border transition duration-300 cursor-pointer",
                      isSelected
                        ? "bg-white border-greyLight"
                        : "border-greyLight/50 hover:bg-white/80"
                    )}
                    key={`option-${option}`}
                  >
                    <div className="flex justify-between">
                      <Radio
                        checked={isSelected}
                        className="font-bold"
                        label={
                          <>
                            <span>
                              {t(`withdrawalModal.options.${option}.title`)}
                            </span>
                            <br />
                            <span className="font-normal text-base mt-1">
                              <Trans
                                i18nKey={`withdrawalModal.options.${option}.description`}
                                components={{ br: <br /> }}
                              />
                            </span>
                          </>
                        }
                        onChange={() => setSelectedOption(option)}
                      />
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-center items-center w-full pt-8">
              <Button
                disabled={!selectedOption}
                isLoading={isWorking}
                onClick={handleContinueClick}
                variant="primary"
              >
                {t("withdrawalModal.buttons.continue")}
              </Button>
            </div>
          </>
        )}
        {page === PAGE.FIND_ORDER && (
          <>
            <h2 className="flex items-center gap-4 text-3xl/snug font-semibold uppercase m-0 mb-4 text-black pr-8">
              <button
                className="size-10 grid place-items-center cursor-pointer hover:scale-105 active:scale-95 transition duration-150"
                onClick={handleBackClick}
              >
                <ArrowLeftIcon size="md" />
              </button>
              <span>{t("withdrawalModal.heading.findOrder")}</span>
            </h2>
            <p>{t("withdrawalModal.description.findOrder")}</p>
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="flex flex-col w-full gap-4"
            >
              <div className="flex flex-col self-start gap-1 w-full">
                <label
                  className="text-sm text-primaryDark font-semibold"
                  htmlFor="withdrawOrderId"
                >
                  {t("withdrawalModal.findOrder.orderId.label")} *
                </label>
                <input
                  className="w-full p-2 bg-white border border-greyMedium rounded-sm text-sm/5"
                  required
                  placeholder={t(
                    "withdrawalModal.findOrder.orderId.placeholder"
                  )}
                  type="text"
                  id="withdrawOrderId"
                  name="orderId"
                  ref={register}
                />
              </div>
              <div className="flex flex-col self-start gap-1 w-full">
                <label
                  className="text-sm text-primaryDark font-semibold"
                  htmlFor="withdrawOrderEmail"
                >
                  {t("withdrawalModal.findOrder.orderEmail.label")} *
                </label>
                <input
                  className="w-full p-2 bg-white border border-greyMedium rounded-sm text-sm/5"
                  required
                  placeholder={t(
                    "withdrawalModal.findOrder.orderEmail.placeholder"
                  )}
                  type="email"
                  id="withdrawOrderEmail"
                  name="email"
                  ref={register}
                />
              </div>
              {isSearchError && (
                <div className="border-2 border-red text-red rounded-lg px-4 py-2 flex items-center gap-2 text-sm">
                  <Icon className="size-5 text-red" icon={<WarningIcon />} />
                  <span>{t("withdrawalModal.findOrder.searchError")}</span>
                </div>
              )}
              <div className="flex justify-center items-center w-full pt-4">
                <Button
                  type="submit"
                  disabled={isWorking}
                  isLoading={isWorking}
                  variant="primary"
                >
                  {t("withdrawalModal.buttons.findOrder")}
                </Button>
              </div>
            </form>
          </>
        )}
        {page === PAGE.B2B_ORDER && (
          <>
            <h2 className="text-3xl/snug font-semibold uppercase m-0 mb-4 text-black pr-8">
              {t("withdrawalModal.heading.b2bOrder")}
            </h2>
            <p className="text-primaryDark m-0 [&+&]:mt-6 sm:text-base/snug">
              {t("withdrawalModal.description.b2bOrder")}
            </p>

            <div className="flex justify-center items-center w-full pt-8">
              <Button onClick={handleExitClick} variant="primary">
                {t("withdrawalModal.buttons.startOver")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
